/**
 * Edit a tracking URL.
 *
 * Creating one happens on its own screen, where the UTM builder has room; this
 * is for changing a link that already exists. The short code is shown and not
 * editable — it is printed on posters and sitting in messages already sent, so
 * changing it would break every one of them.
 */
import { useEffect, useState } from 'react';
import { useApp } from '../../store/AppContext.jsx';
import api from '../../lib/api.js';
import { changed, previewUrl, validDestination } from '../../lib/urltracking.js';
import Sheet, { Group, Field, OptionSelect } from '../Sheet.jsx';
import { SOURCES, MEDIUMS, LINK_STATUS } from '../../constants/urltracking.js';
import { PresetInput, CopyUrl } from '../../pages/urltracking/TrackBits.jsx';

export default function TrackLinkSheet({ id, onClose }) {
  const { toast } = useApp();
  const [row, setRow] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.urlTracking.links.get(id),
      api.urlTracking.campaigns.list({ limit: 500 })
    ])
      .then(([link, list]) => {
        setRow(link);
        setCampaigns((list.data || []).filter(c => c.status !== 'Archived'));
        setForm({
          campaign_id: link.campaign_id,
          name: link.name || '',
          description: link.description || '',
          destination_url: link.destination_url || '',
          utm_source: link.utm_source || '',
          utm_medium: link.utm_medium || '',
          utm_campaign: link.utm_campaign || '',
          utm_term: link.utm_term || '',
          utm_content: link.utm_content || '',
          status: link.status || 'Active'
        });
      })
      .catch(e => toast(e.message));
  }, [id, toast]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const save = async () => {
    if (!validDestination(form.destination_url)) {
      toast('The destination must be a full http:// or https:// address');
      return;
    }
    setSaving(true);
    try {
      await api.urlTracking.links.update(id, form);
      changed();
      toast('Tracking URL saved');
      onClose();
    } catch (e) {
      toast(e.message);
    } finally {
      setSaving(false);
    }
  };

  if (!form) {
    return (
      <Sheet icon="link" tone="g-cyan" title="Tracking URL" onClose={onClose}>
        <p className="muted">Loading…</p>
      </Sheet>
    );
  }

  return (
    <Sheet
      icon="link"
      tone="g-cyan"
      title="Edit tracking URL"
      onClose={onClose}
      onSave={save}
      saveLabel={saving ? 'Saving…' : 'Save changes'}
    >
      <Group>The short link</Group>
      <div className="f wide">
        <div className="utmadeurl flat">
          <code>{row.short_url}</code>
          <CopyUrl url={row.short_url} />
        </div>
        <small className="dim">
          Fixed for the life of the link. Everything below can change without breaking it — including
          the destination, which is how a link already printed somewhere can be pointed elsewhere.
        </small>
      </div>

      <Group>Basics</Group>
      <Field label="Campaign" wide>
        <select value={form.campaign_id} onChange={e => set('campaign_id', e.target.value)}>
          {campaigns.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
      </Field>
      <Field label="URL name" wide>
        <input value={form.name} onChange={e => set('name', e.target.value)} />
      </Field>
      <Field
        label="Destination URL"
        wide
        hint={form.destination_url && !validDestination(form.destination_url)
          ? <small className="utbad">That is not a full http:// or https:// address.</small>
          : null}
      >
        <input value={form.destination_url} onChange={e => set('destination_url', e.target.value)} />
      </Field>
      <Field label="Status">
        <OptionSelect value={form.status} onChange={v => set('status', v)} options={LINK_STATUS} />
      </Field>

      <Group>UTM tags</Group>
      <Field label="Source">
        <PresetInput value={form.utm_source} onChange={v => set('utm_source', v)} options={SOURCES} />
      </Field>
      <Field label="Medium">
        <PresetInput value={form.utm_medium} onChange={v => set('utm_medium', v)} options={MEDIUMS} />
      </Field>
      <Field label="Campaign tag">
        <input value={form.utm_campaign} onChange={e => set('utm_campaign', e.target.value)} />
      </Field>
      <Field label="Term">
        <input value={form.utm_term} onChange={e => set('utm_term', e.target.value)} />
      </Field>
      <Field label="Content" wide>
        <input value={form.utm_content} onChange={e => set('utm_content', e.target.value)} />
      </Field>

      <div className="f wide">
        <label>Visitors will land on</label>
        <code className="utpreview">{previewUrl(form.destination_url, form)}</code>
      </div>

      <p className="utnote">
        Worth knowing: the breakdowns read a link&apos;s tags as they are now, not as they were when
        each click arrived. Retagging this link moves its whole click history to the new source or
        medium. To compare two versions of a message, make a second link rather than editing this one.
      </p>
    </Sheet>
  );
}
